/**
 * MindfulnessProgramsScreen - Lists mindfulness program templates by category
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { Theme } from '../../../Theme/Theme';
import { MindfulnessProgramTemplateCategory, MindfulnessProgramTemplateCategoryApi } from '../../../Model/Coach/Mindfulness/Template/MindfulnessProgramTemplateCategory';
import { MindfulnessProgramCategorySection } from './MindfulnessProgramCategorySection';
import { MindfulnessProgram, MindfulnessProgramApi } from '../../../Model/Coach/Mindfulness/MindfulnessProgram';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  min-height: calc(100vh - 64px);
  background: ${Theme.Colors.background};
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  background: linear-gradient(135deg, #8B5CF6, #7C3AED);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const CategoriesSection = styled.div`
  padding: 32px 0 48px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${Theme.Colors.charcoal};
`;

export type MindfulnessProgramsScreenProps = {};

export const MindfulnessProgramsScreen: React.FC<MindfulnessProgramsScreenProps> = observer(() => {
  const { t } = useTrans();

  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<MindfulnessProgramTemplateCategory[]>([]);
  const [activePrograms, setActivePrograms] = useState<MindfulnessProgram[]>([]);

  useEffect(() => {
    Promise.all([
      MindfulnessProgramTemplateCategoryApi.find(),
      MindfulnessProgramApi.currentlyActive(),
    ])
      .then(([cats, programs]) => {
        setCategories(cats);
        setActivePrograms(programs);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <ScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <AccentText>{t('mindfulnessPrograms.hero.accent', { defaultValue: 'Mindfulness' })}</AccentText>{' '}
            {t('mindfulnessPrograms.hero.title', { defaultValue: 'Programs' })}
          </HeroTitle>
          <HeroSubtitle>
            {t('mindfulnessPrograms.hero.subtitle', {
              defaultValue: 'Guided programs to help you build lasting meditation and mindfulness habits.',
            })}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <CategoriesSection>
        <Container>
          {loading ? (
            <LoadingContainer>
              {t('common.loading', { defaultValue: 'Loading...' })}
            </LoadingContainer>
          ) : (
            categories.map((category) => (
              <MindfulnessProgramCategorySection
                key={category.id}
                category={category}
                activePrograms={activePrograms}
              />
            ))
          )}
        </Container>
      </CategoriesSection>
    </ScreenContainer>
  );
});
